import React, { useEffect } from 'react'
import { useDisclosure, Box, VStack, HStack, Text, GridItem, Img, IconButton } from '@chakra-ui/react'
import FavoriteIcon from 'assets/icons/favorite.svg?react'
import imgPlaceholder from 'assets/images/missing_image.png'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { useNavigate } from 'react-router-dom'
import CollectionOverview from 'components/Pages/CollectionsPage/CollectionOverview'
import ProcessSummary from 'components/Pages/Common/ProcessSummary'
import SvgIcon from 'components/SvgIcon'
import { S3FileTypeEnum } from 'constants/aws'
import { IFavoriteWithRelations } from 'interfaces/favorite'
import { EIconDefaultId } from 'interfaces/iconBuilder'
import IconBuilder from 'pages/IconBuilderPage/components/IconBuilder'
import routes from 'routes'
import { getValidArray } from 'utils/common'
import { getS3FileUrl } from 'utils/images'
import { ITheme } from '../../../../interfaces/theme'
import DashboardSectionCard from '../DashboardSectionCard'
import ModalFavoriteView from '../ModalFavoriteView'
import styles from './styles.module.scss'

const FavoriteView = () => {
  const [selectedCollectionId, setSelectedCollectionId] = React.useState<number>(0)
  const { isOpen: isOpenDashboard, onOpen: onOpenDashboard, onClose: onCloseDashboard } = useDisclosure()
  const { isOpen: isOpenProcessDetail, onOpen: onOpenProcessDetail, onClose: onCloseProcessDetail } = useDisclosure()
  const {
    isOpen: isOpenCollectionDetail,
    onOpen: onOpenCollectionDetail,
    onClose: onCloseCollectionDetail
  } = useDisclosure()
  const { processStore, favoriteStore, iconBuilderStore, organizationStore } = useStores()
  const { organization } = organizationStore
  const currentTheme: ITheme = organization?.theme ?? {}
  const { favorites } = favoriteStore
  const navigate = useNavigate()

  const mostRecentFavorite: IFavoriteWithRelations[] = getValidArray<IFavoriteWithRelations>(favorites).slice(0, 8)

  async function handleOpenProcessDetail(processId: number): Promise<void> {
    processStore.setSelectedProcessId(processId)
    onOpenProcessDetail()
  }

  function handleOpenCollectionDetail(collectionId: number): void {
    if (collectionId) {
      setSelectedCollectionId(collectionId)
      onOpenCollectionDetail()
    }
  }
  function onImgError(evt: React.SyntheticEvent<HTMLImageElement, Event>): void {
    if (evt.currentTarget.src !== imgPlaceholder) {
      evt.currentTarget.src = imgPlaceholder
    }
  }

  useEffect(() => {
    favoriteStore.fetchFavoriteList({
      include: [
        {
          relation: 'collection'
        },
        {
          relation: 'process',
          scope: {
            include: [
              {
                relation: 'documentType'
              }
            ]
          }
        }
      ],
      order: ['createdAt DESC']
    })
  }, [])

  return (
    <>
      <DashboardSectionCard
        icon={<FavoriteIcon />}
        title="Your favorites"
        viewMoreLink={{
          label: 'View more',
          onClick: onOpenDashboard
        }}
        isEmpty={getValidArray(mostRecentFavorite).length === 0}
        emptyTitle="No favorites yet."
        emptyDescription="You don't have any favorites."
        currentTheme={currentTheme}
      >
        <VStack spacing={0} alignItems="flex-start" alignSelf="flex-start" width="full">
          {getValidArray(mostRecentFavorite).map((item: IFavoriteWithRelations, index: number) => {
            const collectionImage: string = item?.collection?.mainMedia ?? ''
            const collectionUrl: string = collectionImage
              ? getS3FileUrl(S3FileTypeEnum.IMAGE, item.collection?.organizationId ?? 0, collectionImage)
              : imgPlaceholder
            const isProcess: boolean = Number(item?.processId) > 0
            return (
              <GridItem
                width="stretch"
                key={item?.id ?? index}
                gap={2}
                color="gray.700"
                transition="all 0.2s"
                paddingX={4}
                paddingY={3}
                display="flex"
                justifyContent="space-between"
                _hover={{
                  background: 'gray.50',
                  boxShadow: 'sm',
                  color: currentTheme?.primaryColor ?? 'primary.500',
                  transition: 'all 0.2s'
                }}
              >
                <HStack spacing={2} alignItems="flex-start">
                  {isProcess ? (
                    <Box width="40px" className={styles.icon}>
                      <IconBuilder
                        icon={iconBuilderStore.getIconById(
                          item?.process?.documentType?.iconId ?? EIconDefaultId.DOCUMENT_TYPE
                        )}
                        size={40}
                        isActive
                      />
                    </Box>
                  ) : (
                    <Box width="40px" alignItems="flex-start">
                      <Img src={collectionUrl} onError={onImgError} width="40px" height="40px" borderRadius="8px" />
                    </Box>
                  )}
                  <Text
                    fontSize="16px"
                    color="inherit"
                    fontWeight="500"
                    lineHeight="24px"
                    marginBottom={0}
                    cursor="pointer"
                    maxHeight="48px"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    alignSelf="center"
                    onClick={() => {
                      isProcess
                        ? navigate(routes.processes.processId.value(String(item?.processId ?? '')))
                        : navigate(routes.collections.collectionId.value(String(item?.collectionId ?? '')))
                    }}
                  >
                    {isProcess
                      ? (item?.processId ? `${item.processId} | ${item?.process?.name ?? ''}` : (item?.process?.name ?? ''))
                      : (item?.collectionId ? `${item.collectionId} | ${item?.collection?.name ?? ''}` : (item?.collection?.name ?? ''))}
                  </Text>
                </HStack>
                <Box width="32px" height="32px" display={{ base: 'none', md: 'block' }}>
                  <IconButton
                    backgroundColor="#ffffff"
                    aria-label="Quick View"
                    isRound
                    size="sm"
                    border="none"
                    onClick={() =>
                      isProcess
                        ? handleOpenProcessDetail(item?.processId ?? 0)
                        : handleOpenCollectionDetail(item?.collectionId ?? 0)
                    }
                    icon={<SvgIcon size={16} iconName="ic_detail" />}
                    _hover={{ backgroundColor: '#EDF2F7' }}
                  />
                </Box>
                <Box width="24px" height="24px" display={{ base: 'block', md: 'none' }}>
                  <IconButton
                    backgroundColor="#ffffff"
                    aria-label="Quick View"
                    isRound
                    border="none"
                    size="xs"
                    onClick={() =>
                      isProcess
                        ? handleOpenProcessDetail(item?.processId ?? 0)
                        : handleOpenCollectionDetail(item?.collectionId ?? 0)
                    }
                    icon={<SvgIcon size={20} iconName="ic_detail"></SvgIcon>}
                    _hover={{ backgroundColor: '#EDF2F7' }}
                  />
                </Box>
              </GridItem>
            )
          })}
        </VStack>
      </DashboardSectionCard>
      <ProcessSummary isOpen={isOpenProcessDetail} onClose={onCloseProcessDetail} />
      <ModalFavoriteView
        isOpen={isOpenDashboard}
        onClose={onCloseDashboard}
        handleOpenProcessDetail={handleOpenProcessDetail}
        handleOpenCollectionDetail={handleOpenCollectionDetail}
      />

      <CollectionOverview
        collectionId={selectedCollectionId}
        toggle={isOpenCollectionDetail ? onCloseCollectionDetail : onOpenCollectionDetail}
        isOpen={isOpenCollectionDetail}
        isCentered
      />
    </>
  )
}

export default observer(FavoriteView)
